import React, { Fragment } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Grid, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
const styles = cssMap({
	wrapper: {
		marginBottom: token('space.300'),
		width: '100%',
	},
	wrapperRefresh: {
		marginTop: token('space.150'),
	},
	grid: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		gridTemplateColumns: '1fr',
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},

	gridSingleColumn: {
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr',
		},
	},
});

type AgentGroupedListProps<T> = {
	data: {
		groupName?: string;
		agents: T[];
		isLoading?: boolean;
		contentRenderOverride?: React.ReactNode;
	}[];
	renderAgentCard: (item: T, index: number) => React.ReactNode;
	singleColumn?: boolean;
};

export const AgentGroupedList = <T,>({
	data,
	renderAgentCard,
	singleColumn,
}: AgentGroupedListProps<T>): React.JSX.Element => {
	return (
		<Stack space="space.300" xcss={cx(styles.wrapper, styles.wrapperRefresh)} alignBlock="start">
			{data.map(({ groupName, agents, isLoading, contentRenderOverride }, index) => {
				const groupNameRender = groupName && (
					<Heading size="small" as="h2">
						<Text color="color.text.subtlest" size="small" weight="semibold">
							{groupName}
						</Text>
					</Heading>
				);

				if (contentRenderOverride) {
					return (
						<Fragment key={index}>
							{groupNameRender}
							{contentRenderOverride}
						</Fragment>
					);
				}

				if (!agents?.length && !isLoading) {
					return null;
				}

				return (
					<Stack key={index} space="space.075" alignBlock="start">
						{groupNameRender}
						<Grid
							gap="space.200"
							xcss={cx(styles.grid, singleColumn && styles.gridSingleColumn)}
							as={agents?.length > 0 ? 'ul' : undefined}
						>
							{isLoading ? (
								<>
									<Skeleton
										width="100%"
										height={98}
										isShimmering
										borderRadius={token('radius.large')}
										testId="agent-grouped-list-skeleton-1"
									/>
									<Skeleton
										width="100%"
										height={98}
										isShimmering
										borderRadius={token('radius.large')}
									/>
									<Skeleton
										width="100%"
										height={98}
										isShimmering
										borderRadius={token('radius.large')}
									/>
								</>
							) : (
								agents?.map((agent, agentIndex) => {
									return renderAgentCard(agent, agentIndex);
								})
							)}
						</Grid>
					</Stack>
				);
			})}
		</Stack>
	);
};
